import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/lib/api";
import type { Esquema } from "@/lib/contrato";
import { flowKeys } from "@/features/operational-flow/api";

import { LISA_20X20_REFERENCE_ID } from "./types";

import type {
  CambioDeMargen,
  City,
  CustomerLife,
  Customer,
  CustomerPage,
  CustomerType,
  NewCustomer,
  ColorApproval,
  ColorRow,
  MargenCambiado,
  NewOrderFromQuote,
  NewQuote,
  NewQuoteLine,
  NuevaObra,
  OrderWithClocks,
  Page,
  PaymentPage,
  PedidoAbierto,
  ProductReference,
  Project,
  QuoteDetail,
  QuotePage,
  QuoteCopy,
  QuotePromise,
  QuoteSent,
  QuoteSort,
  QuoteStatus,
  EntradaAPlanta,
  ToProduction,
  SiigoCatalogs,
} from "./types";

export const keys = {
  customers: (p: CustomerParams) => ["customers", p] as const,
  quotations: (p: QuoteParams) => ["quotations", p] as const,
  quotation: (id: string) => ["quotations", id] as const,
  life: (id: string) => ["customers", id, "life"] as const,
  customerCatalogs: ["customers", "catalogs"] as const,
};

export type CustomerParams = {
  q?: string;
  type?: CustomerType;
  limit: number;
  offset: number;
};

/**
 * Los terceros, **buscando y paginando en el SERVIDOR**.
 *
 * Son 3.534 y el máximo por petición son 500: esta lista no se trae entera, y ninguna
 * pantalla puede fingir que sí. Filtrar o buscar en el navegador aquí daría lo de siempre —
 * el filtro miraría la página y no el conjunto— solo que con setenta veces más filas
 * escondidas detrás.
 *
 * `keepPreviousData` sostiene la página anterior mientras llega la siguiente: sin eso, la
 * lista se vacía con cada letra que se escribe y se lee como si el dato fuera inestable.
 */
export function useCustomers(params: CustomerParams) {
  return useQuery({
    queryKey: keys.customers(params),
    queryFn: () => {
      const s = new URLSearchParams();
      if (params.q) s.set("q", params.q);
      if (params.type) s.set("type", params.type);
      s.set("limit", String(params.limit));
      s.set("offset", String(params.offset));
      return apiGet<CustomerPage>(`/customers?${s.toString()}`);
    },
    placeholderData: keepPreviousData,
  });
}

/** La ficha de Terceros solo usa catálogos locales; abrirla no consulta Siigo. */
export const useCustomerCatalogs = () =>
  useQuery({
    queryKey: keys.customerCatalogs,
    queryFn: () => apiGet<Pick<SiigoCatalogs, "id_types" | "fiscal_responsibilities">>("/siigo/catalogs?local_only=true"),
    staleTime: 10 * 60_000,
  });

/**
 * Las ciudades. **De nuestra base**, así que ni dependen de que Siigo esté en pie ni gastan
 * peticiones de su límite. Sin `q` se obtiene el catálogo completo, sin el corte anterior300.
 */
export function useCities(q: string) {
  return useQuery({
    queryKey: ["cities", q] as const,
    queryFn: () => apiGet<{ rows: City[]; total: number }>(`/cities?q=${encodeURIComponent(q)}&limit=10000`),
    staleTime: 10 * 60_000,
  });
}

/** Identidad geográfica compartida por selector/listado; nunca sustituye los códigos RAW del DTO. */
export const cityKey = (city: Pick<Customer, "country_code" | "state_code" | "city_code">) =>
  JSON.stringify([city.country_code, city.state_code, city.city_code]);

function useRefreshCustomers() {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: ["customers"] });
}

/** Compatibilidad del selector por tripleta; no sustituye autenticación ni permisos. */
const GEO_IDENTITY = { headers: { "X-NECA-Geo-Identity": "1" } };

/**
 * Corrige un tercero.
 *
 * **Un tercero a medio llenar es normal**: quien lo crea sabe el nombre y el documento, y los
 * datos fiscales los trae la asistente después. Lo que no es normal es que no haya camino de
 * vuelta — así se quedó «daniela Salas», con dirección y sin ciudad, sin forma de poner la una
 * ni de quitar la otra. El estado existía y no llevaba a ningún sitio.
 *
 * **Esto NO toca Siigo.** Si el tercero ya está allá, corregirlo aquí lo deja distinto allí, y
 * la pantalla tiene que decirlo o alguien va a creer que se actualizó solo.
 */
export function useUpdateCustomer(id: string) {
  const refresh = useRefreshCustomers();
  return useMutation({
    mutationFn: (body: Partial<NewCustomer>) => apiPatch<Customer>(`/customers/${id}`, body, GEO_IDENTITY),
    onSuccess: refresh,
  });
}

export function useCreateCustomer() {
  const refresh = useRefreshCustomers();
  return useMutation({
    mutationFn: (body: NewCustomer) => apiPost<Customer>("/customers", body, GEO_IDENTITY),
    onSuccess: refresh,
  });
}

/**
 * Lo que responde `POST /customers/siigo-lookup` (contrato `CustomerLookup`). El servidor declara
 * `customer` como objeto abierto; trae la misma fila que `GET /customers/{id}`, por eso es `Customer`.
 */
export type CustomerLookup = Omit<Esquema<"CustomerLookup">, "customer"> & { customer: Customer };

/**
 * **«Traer de Siigo» un tercero por su NIT** (D-053, decisión del owner 11-oct). Primero el ERP; si
 * no lo tiene, el servidor hace UNA lectura a Siigo y lo crea. Una mutación por clic: nunca se
 * reintenta sola (`retry` de las mutaciones es 0), porque cada intento gasta el cupo de la empresa.
 */
export function useSiigoLookup() {
  const refresh = useRefreshCustomers();
  return useMutation({
    mutationFn: (nit: string) => apiPost<CustomerLookup>("/customers/siigo-lookup", { nit }),
    onSuccess: refresh,
  });
}

/* ── F1 · Cotizaciones y la hoja de vida del cliente ───────────────────────────── */

export type QuoteParams = {
  customer_id?: string;
  project_id?: string;
  status?: QuoteStatus;
  q?: string;
  /** Solo las archivadas (pestaña «Archivadas»); sin él, el servidor da las que no lo están. */
  archived?: boolean;
  /** El orden lo pone el servidor: `priority` primero la de más valor; sin él, las más recientes. */
  sort?: QuoteSort;
  limit: number;
  offset: number;
};

/**
 * Las cotizaciones, **filtrando y paginando en el servidor**, igual que los terceros.
 *
 * `keepPreviousData` no es adorno: sin él la lista se vacía con cada letra que se teclea en el
 * buscador, y una lista que parpadea se lee como si el dato fuera inestable.
 */
export function useQuotations(params: QuoteParams) {
  return useQuery({
    queryKey: keys.quotations(params),
    queryFn: () => {
      const s = new URLSearchParams();
      if (params.customer_id) s.set("customer_id", params.customer_id);
      if (params.project_id) s.set("project_id", params.project_id);
      if (params.status) s.set("status", params.status);
      if (params.q) s.set("q", params.q);
      if (params.archived) s.set("archived", "true");
      if (params.sort && params.sort !== "recent") s.set("sort", params.sort);
      s.set("limit", String(params.limit));
      s.set("offset", String(params.offset));
      return apiGet<QuotePage>(`/quotations?${s.toString()}`);
    },
    placeholderData: keepPreviousData,
  });
}

/** Una cotización con sus líneas y los pedidos que salieron de ella. */
export function useQuotation(id: string | null) {
  return useQuery({
    queryKey: keys.quotation(id ?? ""),
    queryFn: () => apiGet<QuoteDetail>(`/quotations/${id}`),
    enabled: id != null,
  });
}

/**
 * La hoja de vida del cliente: la raíz del árbol de la F1.
 *
 * Trae las obras con sus cotizaciones y sus pedidos, las cotizaciones que todavía no tienen
 * obra, y el resumen. Una sola petición para todo el árbol: pedir cada rama por separado haría
 * que la pantalla se armara a pedazos y que el total de arriba no cuadrara con lo de abajo
 * mientras llegan.
 */
export function useCustomerLife(customerId: string | null) {
  return useQuery({
    queryKey: keys.life(customerId ?? ""),
    queryFn: () => apiGet<CustomerLife>(`/customers/${customerId}/life`),
    enabled: customerId != null,
  });
}

/**
 * Lo que hay que volver a pedir después de tocar una cotización.
 *
 * Se invalida **ancho a propósito**: una cotización que se gana cambia su ficha, la lista, la
 * hoja de vida de su cliente y la lista de pedidos. Afinar esto para ahorrar una petición es
 * cómo se acaba con una pantalla que enseña una cifra vieja al lado de una nueva.
 */
function useRefreshSales() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ["quotations"] });
    void client.invalidateQueries({ queryKey: ["customers"] });
    void client.invalidateQueries({ queryKey: ["orders"] });
  };
}

export function useCreateQuote() {
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (body: NewQuote) => apiPost<QuoteDetail>("/quotations", body),
    onSuccess: refresh,
  });
}

/** Corregir el borrador. Si la cotización ya salió, el servidor solo acepta `status` y `note`. */
export function usePatchQuote(id: string) {
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (body: Partial<Pick<NewQuote, "project_id" | "issued_on" | "valid_until" | "general_days" | "note">> & { status?: QuoteStatus }) =>
      apiPatch<QuoteDetail>(`/quotations/${id}`, body),
    onSuccess: refresh,
  });
}

/**
 * Reemplaza las líneas del borrador **enteras**, no una a una. Las que ya existían van con su `id`
 * (D-060 K1) para que el servidor les conserve el dibujo si no cambió la referencia.
 */
export function useReplaceQuoteLines(id: string) {
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (lines: NewQuoteLine[]) => apiPut<QuoteDetail>(`/quotations/${id}/lines`, { lines }),
    onSuccess: refresh,
  });
}

export function useDeleteQuote() {
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/quotations/${id}`),
    onSuccess: refresh,
  });
}

/**
 * **Archivar o desarchivar** (contrato «Guardar = copia en Siigo», 14-oct): no la borra y en Siigo
 * queda igual. `POST /quotations/{id}/archive` con `{ reason }` o `DELETE …/archive`; las dos
 * responden el detalle. Pide `cotizaciones.editar`.
 */
export function useArchiveQuote(id: string) {
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (archived: boolean) => archived
      ? apiPost<QuoteDetail>(`/quotations/${id}/archive`, { reason: null })
      : apiDelete<QuoteDetail>(`/quotations/${id}/archive`),
    onSuccess: refresh,
  });
}

/**
 * Anotar el número que le puso Siigo.
 *
 * **No le habla a Siigo**: solo apunta el número que ya existe allá. El cupo de la API es de la
 * empresa y no del programa, así que ninguna pantalla lo gasta por su cuenta.
 */
export function useQuoteSent(id: string) {
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (body: QuoteSent) => apiPost<QuoteDetail>(`/quotations/${id}/sent`, body),
    onSuccess: refresh,
  });
}

/**
 * Lo que ya salió no se toca (la base lo sella): se copia. Un borrador NUEVO del mismo cliente y obra
 * con todas las líneas, sin número; con `replaces`, la nueva versión que reemplaza a esta (sin pedido).
 */
export function useCopyQuote(id: string) {
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (body: QuoteCopy) => apiPost<QuoteDetail>(`/quotations/${id}/copy`, body),
    onSuccess: refresh,
  });
}

/**
 * **El primer abono, que hace cuatro cosas de una vez.**
 *
 * Anota el pago, crea el pedido con su código, fija la fecha prometida —el abono más los días
 * hábiles— y pasa la cotización a ganada. Por eso el botón que lo dispara no dice «registrar un
 * pago»: diría que anota una cifra cuando en realidad compromete una fecha de entrega.
 *
 * Y **no mete la obra en la planta**. Eso es el otro acto, `useToProduction`, y lo decide el
 * jefe de producción. Entre los dos está la fila, que es el dato que hoy no existe.
 */
export function useFirstPayment(quoteId: string) {
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (body: NewOrderFromQuote) => apiPost<PedidoAbierto>(`/quotations/${quoteId}/orders`, body),
    onSuccess: refresh,
  });
}

/** La entrada a la planta: arranca el segundo reloj. */
export function useToProduction(orderId: string) {
  const client = useQueryClient();
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (body: ToProduction) => apiPost<EntradaAPlanta>(`/orders/${orderId}/production`, body),
    onSuccess: async () => {
      refresh();
      await Promise.all([
        client.invalidateQueries({ queryKey: ["order-timeline", orderId] }),
        client.invalidateQueries({ queryKey: flowKeys }),
      ]);
    },
  });
}

/**
 * **Cuánto de más se fabrica en este pedido** (0056).
 *
 * `null` devuelve el pedido al margen de su línea; `0` dice «nada de más, aunque la línea diga
 * otra cosa». Son dos decisiones distintas y el servidor las guarda distintas.
 *
 * Rehace las piezas de las órdenes que se siguen fabricando, así que invalida producción además de
 * ventas: el cronograma y la ficha del pedido enseñan esas piezas.
 */
export function useCambiarMargenDelPedido(orderId: string) {
  const client = useQueryClient();
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (body: CambioDeMargen) =>
      apiPatch<MargenCambiado>(`/orders/${orderId}/overproduction`, body),
    onSuccess: () => {
      refresh();
      void client.invalidateQueries({ queryKey: ["production"] });
      void client.invalidateQueries({ queryKey: ["order-timeline"] });
    },
  });
}

/* ── Los cobros ──────────────────────────────────────────────────────────────── */

/** Los cobros de un pedido, del más antiguo al más nuevo. */
export function usePayments(orderId: string | null) {
  return useQuery({
    queryKey: ["payments", orderId] as const,
    queryFn: () => apiGet<PaymentPage>(`/payments?order_id=${orderId}&order=paid_on`),
    enabled: orderId != null,
  });
}

/*
 * **Aquí había un `useAddPayment` que nadie usaba, y por eso se fue.**
 *
 * Iba contra el `/payments` genérico, que **no tiene tope**: en la prueba de punta a punta aceptó
 * cinco millones en un pedido ya pagado del todo. Cobrar vive ahora en `pedidos/api.ts`, contra
 * `/orders/{id}/payments`, que sí lo tiene.
 *
 * Y se borra en vez de dejarlo «por si acaso»: un enganche sin pantalla que lo use no es trabajo
 * hecho, es **deuda con aspecto de trabajo hecho** — el siguiente que busque cómo cobrar lo
 * encuentra, lo usa, y se lleva la ruta sin tope.
 */

/**
 * Qué fecha se prometería si el abono entrara ese día.
 *
 * Se pide **cada vez que cambia la fecha en el formulario**, y la cuenta la hace el servidor con
 * el mismo código que luego la guarda. Aquí no se cuentan días hábiles: hacerlo obligaría a
 * tener la tabla de festivos colombianos en dos sitios, y el día que discreparan una de las dos
 * le estaría mintiendo a quien hizo la promesa.
 */
export function useQuotePromise(
  quoteId: string | null,
  paidOn: string | null,
  colorApprovedOn?: string | null,
  /** Probar otro plazo antes de abrir el pedido (#13): otros días hábiles o una fecha fija. */
  plazo?: { general_days: number } | { delivers_on: string },
) {
  return useQuery({
    queryKey: ["quotations", quoteId, "promise", paidOn, colorApprovedOn ?? "", plazo ?? null] as const,
    queryFn: () => {
      const s = new URLSearchParams();
      if (paidOn) s.set("paid_on", paidOn);
      // Sin aprobación, el servidor contesta `color_pending: true` y una fecha que la SUPONE el
      // mismo día del abono. Mandarla cuando se conoce cambia la fecha, así que viaja igual.
      if (colorApprovedOn) s.set("color_approved_on", colorApprovedOn);
      // Con días, la fecha fija guardada no cuenta (`fixed=false`): se están probando los días.
      if (plazo && "general_days" in plazo) {
        s.set("general_days", String(plazo.general_days));
        s.set("fixed", "false");
      }
      if (plazo && "delivers_on" in plazo) s.set("delivers_on", plazo.delivers_on);
      const q = s.toString();
      return apiGet<QuotePromise>(`/quotations/${quoteId}/promise${q ? `?${q}` : ""}`);
    },
    enabled: quoteId != null,
    // Sostiene la fecha anterior mientras llega la nueva: sin esto, el aviso de «comprometes el
    // 5 de noviembre» desaparece con cada tecla y el formulario parpadea justo donde hay que
    // leer con cuidado.
    placeholderData: keepPreviousData,
  });
}

/* ── Los desplegables del formulario · todos locales, ninguno toca Siigo ───────── */

/**
 * Las obras de un cliente.
 *
 * Hace falta al abrir el pedido: **un pedido siempre es de una obra**, y si la cotización no
 * trae ninguna, hay que decirla ahí. Sin esto el servidor devuelve un 422 y la persona no sabe
 * qué le falta.
 */
export function useProjects(customerId: string | null) {
  return useQuery({
    queryKey: ["projects", customerId] as const,
    queryFn: () => apiGet<Page<Project>>(`/projects?customer_id=${customerId}&limit=200`),
    enabled: customerId != null,
  });
}

/**
 * Las referencias del catálogo LOCAL.
 *
 * **No es `/api/siigo/products`**, que es lo que usaba el formulario viejo. Buscar allá es una
 * petición a Siigo —cupo de la empresa, y bloquea el usuario si se abusa— y encima no serviría:
 * una línea de cotización pide una `reference_id` de esta base, no un código de Siigo.
 *
 * **Sin las estándar de Pegante, Fundidos y Cerámicos** (`is_line_standard=false`, N-038): llevan
 * su color o receta, que solo se escoge en su pestaña del Cotizador; atadas aquí, el servidor
 * respondería 422.
 */
export function useProductReferences(q: string) {
  return useQuery({
    queryKey: ["product-references", q] as const,
    queryFn: () => {
      const s = new URLSearchParams({ is_active: "true", is_line_standard: "false", limit: "50" });
      if (q) s.set("q", q);
      return apiGet<Page<ProductReference>>(`/product-references?${s.toString()}`);
    },
    placeholderData: keepPreviousData,
  });
}

/** Los colores de la carta. */
export function useColors(q: string) {
  return useQuery({
    queryKey: ["colors", q] as const,
    queryFn: () => {
      const s = new URLSearchParams({ limit: "100" });
      if (q) s.set("q", q);
      return apiGet<Page<ColorRow>>(`/colors?${s.toString()}`);
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * **La referencia neutra de la lisa, solo para saber su formato.**
 *
 * El costeo de la lisa se pide por formato, y la línea de cotización solo guarda la referencia.
 * Esta consulta es el puente. Va por id a la ficha de la referencia —no a la lista— porque así
 * sirve igual al crear una cotización y al corregir un borrador: en el borrador la línea llegó
 * del servidor y nadie buscó la referencia en un listado del que pudiera sacarse el formato.
 *
 * `enabled` la apaga en las líneas que no son la lisa, que son casi todas. Sin eso, abrir el
 * formulario pediría el costeo de una baldosa que nadie está cotizando.
 */
export function useLisaReference(enabled: boolean) {
  return useQuery({
    queryKey: ["product-references", "ficha", LISA_20X20_REFERENCE_ID] as const,
    queryFn: () => apiGet<ProductReference>(`/product-references/${LISA_20X20_REFERENCE_ID}`),
    enabled,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * **Anotar que el cliente aprobó el color.**
 *
 * Es lo que hace arrancar el reloj general. La cotización promete «45 días hábiles después del
 * abono **y** la aprobación del color», así que el plazo cuenta desde la última de las dos
 * fechas. Hasta que esto se anota, `promised_on` es `null` y no hay fecha que prometer — y eso
 * es correcto, no un hueco: sin la aprobación no existe todavía.
 */
export function useColorApproval(orderId: string) {
  const refresh = useRefreshSales();
  return useMutation({
    mutationFn: (body: ColorApproval) => apiPost<OrderWithClocks>(`/orders/${orderId}/color-approval`, body),
    onSuccess: refresh,
  });
}

/**
 * **Crear una obra.**
 *
 * Devuelve la fila entera, así que la obra recién creada se deja elegida sin volver a pedir la
 * lista — que en mitad de un cobro es la diferencia entre seguir y esperar.
 *
 * La base impide dos obras con el mismo nombre para un cliente, **sin distinguir mayúsculas**.
 * El aviso se da antes de intentarlo; el 409 es la red de seguridad, no el camino normal.
 */
export function useCrearObra() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: NuevaObra) => apiPost<Project>("/projects", body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["projects"] });
      void client.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

/* ───────────────────────────────────────────────────────────────────────────────────────────
   El resumen de Comercial · fase 5 del plan de la lisa 20×20
   ─────────────────────────────────────────────────────────────────────────────────────────── */

import type { ResumenDePedidos } from "./types";

/**
 * **Cómo va lo que se vendió, sin entrar pedido por pedido.**
 *
 * Una sola petición: `GET /api/orders/commercial-summary`. El servidor deduce la etapa de cada
 * pedido —por mandar, en producción, listo—, marca el distintivo del 80 % prensado y redacta los
 * avisos. Aquí no se cuenta nada: contar los estados en el navegador significaría tener la regla
 * escrita dos veces y, el día que cambiara una, dos pantallas diciendo cosas distintas.
 *
 * `line` es opcional; con la abreviatura de la línea (`BA`) o su código largo (`tile`). Una línea
 * que no existe da **404**, no una lista vacía: eso lo dice el `QueryState`.
 *
 * `enabled` está para no preguntar a quien no tiene `pedidos.ver`: pedirlo igual le pintaría un
 * cartel de permiso en mitad de su pantalla en vez de la explicación que sí le sirve.
 */
export function useResumenDePedidos(line?: string | null, enabled = true) {
  return useQuery({
    queryKey: ["orders", "commercial-summary", line ?? ""] as const,
    queryFn: () =>
      apiGet<ResumenDePedidos>(
        `/orders/commercial-summary${line ? `?line=${encodeURIComponent(line)}` : ""}`,
      ),
    enabled,
  });
}

/* ───────────────────────────────────────────────────────────────────────────────────────────
   El precio de venta de la lisa, por color · E5-01
   ─────────────────────────────────────────────────────────────────────────────────────────── */

import type { LisaPriceColor, LisaPrices } from "./types";

/**
 * **El precio de venta por m² de cada color de la lisa, por una puerta que ventas sí puede abrir.**
 *
 * Sustituyó al hook que pedía `/costs/lisa-grid` (ya borrado de este archivo). Aquella ruta exige
 * `costos.ver`, y a quien vende le contestaba **403**: el precio quedaba vacío y la pantalla no
 * decía nada (E5-01, hallazgo de la prueba de punta a punta). Esta pide
 * `GET /api/quotations/lisa-prices`, con `cotizaciones.ver`, y devuelve el **mismo** precio de
 * venta sin costos, sin márgenes y sin materiales.
 *
 * `/costs/lisa-grid` se queda donde le corresponde: en Costeo, para quien ve costos.
 *
 * Se pide una sola vez por formato y se guarda una hora: la política de precio no cambia mientras
 * alguien teclea una cotización, y volver a pedirla en cada línea haría cuatro peticiones para
 * cuatro líneas de la misma lisa.
 */
export function useLisaPrices(formatId: string | null | undefined) {
  return useQuery({
    queryKey: ["quotations", "lisa-prices", formatId ?? ""] as const,
    queryFn: () => apiGet<LisaPrices>(`/quotations/lisa-prices?format_id=${encodeURIComponent(formatId!)}`),
    enabled: !!formatId,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * La celda de un color dentro de la respuesta, comprobando la forma **antes** de leerla.
 *
 * `colors` llega de la red: si el cuerpo no es lo prometido —otra versión de la API, un proxy que
 * devuelve una página de error con estado 200— `colors.find` reventaría la fila entera y el
 * cotizador se quedaría en blanco. Sin celda se sugiere nada, que es lo mismo que pasa cuando el
 * color no está en la carta.
 */
export function celdaDeColor(datos: LisaPrices | undefined, code: string | null): LisaPriceColor | null {
  if (!datos || !code || !Array.isArray(datos.colors)) return null;
  return datos.colors.find((c) => c && c.code === code) ?? null;
}
